export type PersonMatchDetailTab = 'analysis' | 'profile' | 'letter'
export type OpportunityMatchDetailTab = 'analysis' | 'job'
export type MatchDetailTab = PersonMatchDetailTab | OpportunityMatchDetailTab

const PERSON_TABS: PersonMatchDetailTab[] = ['analysis', 'profile', 'letter']
const OPPORTUNITY_TABS: OpportunityMatchDetailTab[] = ['analysis', 'job']

export function parsePersonMatchDetailTab(
  tabParam: string | null | undefined,
): PersonMatchDetailTab | null {
  if (tabParam === 'analysis' || tabParam === 'profile' || tabParam === 'letter') return tabParam
  return null
}

export function parseOpportunityMatchDetailTab(
  tabParam: string | null | undefined,
): OpportunityMatchDetailTab | null {
  if (tabParam === 'analysis' || tabParam === 'job') return tabParam
  return null
}

/** Default tab: analysis when evaluation exists, otherwise fallback. */
export function defaultPersonMatchDetailTab(
  evaluationId: string | null | undefined,
  tabParam?: string | null,
  fallback: PersonMatchDetailTab = 'profile',
): PersonMatchDetailTab {
  const parsed = parsePersonMatchDetailTab(tabParam)
  if (parsed) return parsed
  return evaluationId ? 'analysis' : fallback
}

export function defaultOpportunityMatchDetailTab(
  evaluationId: string | null | undefined,
  tabParam?: string | null,
  fallback: OpportunityMatchDetailTab = 'job',
): OpportunityMatchDetailTab {
  const parsed = parseOpportunityMatchDetailTab(tabParam)
  if (parsed) return parsed
  return evaluationId ? 'analysis' : fallback
}

export function appendMatchDetailQuery(
  basePath: string,
  params: { evaluationId?: string | null; tab?: MatchDetailTab | null },
): string {
  const search = new URLSearchParams()
  if (params.evaluationId) {
    search.set('evaluation', params.evaluationId)
  }
  if (params.tab) {
    search.set('tab', params.tab)
  }
  const qs = search.toString()
  return qs ? `${basePath}?${qs}` : basePath
}

export function buildEmployerPersonMatchPath(
  jobId: string,
  profileId: string,
  options?: { evaluationId?: string | null; tab?: PersonMatchDetailTab | null },
): string {
  const base = `/employer/jobs/${encodeURIComponent(jobId)}/people/${encodeURIComponent(profileId)}`
  const tab =
    options?.tab ??
    (options?.evaluationId ? ('analysis' as const) : null)
  return appendMatchDetailQuery(base, {
    evaluationId: options?.evaluationId,
    tab,
  })
}

export type RecruiterPersonMatchOrigin = 'talent' | 'brief'

export function buildRecruiterPersonMatchPath(
  profileId: string,
  options?: {
    briefId?: string | null
    evaluationId?: string | null
    tab?: PersonMatchDetailTab | null
  },
): string {
  const base =
    options?.briefId != null && String(options.briefId).trim()
      ? `/recruiter/briefs/${encodeURIComponent(String(options.briefId).trim())}/people/${encodeURIComponent(profileId)}`
      : `/recruiter/talent/people/${encodeURIComponent(profileId)}`
  const tab =
    options?.tab ??
    (options?.evaluationId ? ('analysis' as const) : null)
  return appendMatchDetailQuery(base, {
    evaluationId: options?.evaluationId,
    tab,
  })
}

export function buildRecruiterPersonMatchBackPath(
  origin: RecruiterPersonMatchOrigin,
  briefId?: string | null,
): string {
  if (origin === 'brief' && briefId) {
    return `/recruiter/briefs/${encodeURIComponent(briefId)}?tab=search`
  }
  return '/recruiter/talent?tab=search'
}

export function buildCandidateOpportunityMatchPath(
  evaluationId: string,
  options?: { tab?: OpportunityMatchDetailTab | null },
): string {
  const base = `/candidate/jobs/matches/${encodeURIComponent(evaluationId)}`
  const tab = options?.tab ?? 'analysis'
  return appendMatchDetailQuery(base, { tab })
}

export { PERSON_TABS, OPPORTUNITY_TABS }
